/**
 * useTodayItems — the ONE composer for "what is on the owner's plate today".
 *
 * WHY THIS EXISTS
 * ---------------
 * Before this hook there were five independent today-composers (the terminal
 * home rail, the aside toolbar, the classic dashboard's event feed, and the
 * calendar itself), each with its own date parsing, its own idea of which
 * layers count, and its own recurrence handling. They gave different answers
 * for the same day depending on which theme was active: jobs appeared in some
 * and vanished in others, recurring events were dropped entirely by two of
 * them, and the calendar rendered payables that the rail did not.
 *
 * So this hook does NOT re-derive anything. It calls exactly the same item
 * builders the calendar's CalendarContext calls
 * (`eventToCalendarItem` / `invoiceToCalendarItem` / `billToCalendarItem` /
 * `jobToCalendarItem` / `repeatingInvoiceToCalendarItems`) and returns the same
 * `UnifiedCalendarItem` shape, so a row in a rail and a chip in the month grid
 * are literally the same object built by the same code. If they ever disagree
 * about a date, both are wrong in the same way — which is a bug you can find.
 *
 * DATA SOURCE: `useAppData()` only. No new Firestore listeners; every layer
 * here is already streaming into the app for other reasons.
 *
 * THE ONE LAYER THAT IS NOT AUTOMATIC — recurring invoice/bill TEMPLATES live
 * in `RepeatingInvoiceContext`, which is NOT mounted globally (only inside
 * FirebaseCalendar and AccountingPage). `useAppData().repeatingInvoices` is
 * hard-coded to `[]` since accounting went native, so it is NOT a usable
 * source. Callers that sit under a `RepeatingInvoiceProvider` therefore pass
 * `repeatingInvoices` in; callers that do not simply get no template rows,
 * rather than this hook silently opening a listener of its own.
 */

import { useEffect, useMemo, useState } from 'react';
import { useAppData } from '../providers/AppDataProvider';
import { CalendarEvent } from '../pages/job-summary/Interfaces';
import { RepeatingInvoice } from '../types/accounting';
import { XeroInvoice } from '../types/xero';
import {
  UnifiedCalendarItem,
  CalendarItemType,
  CalendarFilterState,
  DEFAULT_CALENDAR_FILTERS,
  eventToCalendarItem,
  invoiceToCalendarItem,
  invoiceCreationToCalendarItem,
  billToCalendarItem,
  jobToCalendarItem,
  repeatingInvoiceToCalendarItems,
  toLocalDateString,
} from '../types/calendar';
import { addFrequency } from '../utils/recurrence';

// ============================================
// Public API
// ============================================

export interface UseTodayItemsOptions {
  /**
   * How many days AFTER today the `later` window reaches, inclusive.
   * 0 = today only. Default 4 (what the home rail has always shown).
   */
  days?: number;
  /**
   * Layer toggles, merged over DEFAULT_CALENDAR_FILTERS. Same shape the
   * calendar uses, so "hide bills" means the same thing on both surfaces.
   */
  filters?: Partial<CalendarFilterState>;
  /**
   * Recurring invoice/bill templates. Only available to callers under a
   * `RepeatingInvoiceProvider` — see the file header for why this cannot be
   * sourced automatically.
   */
  repeatingInvoices?: RepeatingInvoice[];
  /**
   * Include invoices/bills that are already PAID / VOIDED / DELETED.
   *
   * Default false. The calendar plots every document on its due date because a
   * month grid is a record; a day rail is a to-do list, and money that has
   * already moved is not a task. This narrows WHICH documents are listed — it
   * never changes the day a document lands on, so the two surfaces cannot
   * disagree about dates.
   */
  includeSettled?: boolean;
}

export interface UseTodayItemsResult {
  /** Today + later, deduped, in display order. */
  items: UnifiedCalendarItem[];
  /** Items falling on the local current day. */
  today: UnifiedCalendarItem[];
  /** Items in the remainder of the window, i.e. tomorrow .. today+days. */
  later: UnifiedCalendarItem[];
  /**
   * First load of the two layers whose loading flags are trustworthy (events
   * and jobs). The invoice/bill listeners in AppDataProvider never touch
   * `xeroLoading` — it is pinned false from the first frame — so folding it in
   * here would be a confident lie. See CalendarLayerStatus.statusKnown.
   */
  loading: boolean;
  /** The local current day, 'YYYY-MM-DD'. Recomputed when the clock rolls over. */
  todayDay: string;
  /** Last day of the window, inclusive, 'YYYY-MM-DD'. */
  endDay: string;
}

/**
 * The local calendar day an item renders on, 'YYYY-MM-DD'.
 *
 * All-day items store a bare day, timed items a full ISO instant, and some
 * builders emit `parsedDate.toISOString()` for an all-day item — which in
 * Adelaide (+9:30/+10:30) is the PREVIOUS day in UTC. So never slice the
 * string; always come back through the local calendar.
 */
export function calendarItemDay(item: UnifiedCalendarItem): string {
  if (/^\d{4}-\d{2}-\d{2}$/.test(item.start)) return item.start;
  const parsed = new Date(item.start);
  return isNaN(parsed.getTime()) ? '' : toLocalDateString(parsed);
}

/**
 * Tie-break order for items landing on the same day at the same time.
 *
 * The owner's stated daily priority is: client emails > open jobs > accounts
 * payable > Google Ads > everything else. Emails are not calendar items, so
 * the rule that actually bites here is that a payable must never outrank a
 * job, and receivables are explicitly NOT a top priority.
 */
export const TODAY_ITEM_PRIORITY: Record<CalendarItemType, number> = {
  job_due: 0,           // open jobs — the owner's #1 among calendar-shaped work
  event: 1,             // a booked commitment with a wall-clock time
  bill_due: 2,          // accounts payable
  recurring_expense: 3, // a payable that has not been raised yet
  invoice_due: 4,       // receivables — deliberately below jobs and payables
  recurring_invoice: 5,
  invoice_created: 6,   // historical, informational only
};

// ============================================
// Day-string helpers
// ============================================

/** Add whole days to a 'YYYY-MM-DD' at UTC noon — far enough from either
 *  midnight that no DST shift or half-hour offset lands on a neighbouring day. */
function addDayString(day: string, days: number): string {
  const d = new Date(`${day}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

/** Whole days between two 'YYYY-MM-DD' strings (b - a), measured at UTC noon. */
function dayStringDiff(a: string, b: string): number {
  const ms = new Date(`${b}T12:00:00Z`).getTime() - new Date(`${a}T12:00:00Z`).getTime();
  return Math.round(ms / 86400000);
}

/** 'YYYY-MM-DD' -> local midnight, for the builders that still take Dates. */
function dayToLocalDate(day: string): Date {
  const [y, m, d] = day.split('-').map(Number);
  return new Date(y, m - 1, d);
}

// ============================================
// Settled-money test
// ============================================

/** Statuses meaning the money already moved (or the document is void). */
const SETTLED_STATUSES = new Set(['PAID', 'VOIDED', 'DELETED']);

function isSettled(doc: XeroInvoice): boolean {
  return SETTLED_STATUSES.has(String(doc.Status || '').toUpperCase());
}

// ============================================
// Recurring events
//
// MIRRORS the private `generateEventOccurrences` in
// src/app/pages/dashboard/calendar/CalendarContext.tsx — same fixed-step
// fast-forward, same `addFrequency` delegation for MONTHLY/YEARLY, same
// exceptions / recurrenceEnd handling, same caps. It is not exported from
// there, so this is a deliberate second copy: CHANGE ONE, CHANGE THE OTHER,
// or the rail and the grid start disagreeing about which days a series falls
// on — which is the exact class of bug this hook exists to kill.
// ============================================

/** Recurrence rules that advance by a fixed number of days. */
const FIXED_STEP_DAYS: Record<string, number> = { DAILY: 1, WEEKLY: 7 };

/** Steps the catch-up loop may take on a MONTHLY/YEARLY series before giving
 *  up. 1,200 monthly steps is a century — reaching it means malformed data. */
const MAX_CATCHUP_STEPS = 1200;

/** Ceiling on occurrences materialised for one series in one window. The
 *  window here is days, not months, so this is never legitimately reached. */
const MAX_OCCURRENCES = 400;

/** Advance one occurrence day, or null if the rule is not one we understand. */
function stepOccurrenceDay(day: string, recurrence: string, anchorDay: string): string | null {
  const fixed = FIXED_STEP_DAYS[recurrence];
  if (fixed) return addDayString(day, fixed);
  try {
    return addFrequency(day, recurrence, anchorDay);
  } catch {
    return null; // unknown frequency — caller falls back to a one-off event
  }
}

/** One materialised occurrence: the shifted event copy plus the day it falls on. */
interface EventOccurrence {
  event: CalendarEvent;
  day: string;
}

/**
 * Occurrences of a recurring event inside [rangeStartDay, rangeEndDay].
 *
 * Returns null when the rule is not one we understand, which tells the caller
 * to render the event as a plain one-off. An empty array means the rule IS
 * understood and simply produces nothing in this window.
 */
function generateEventOccurrences(
  event: CalendarEvent,
  recurrence: string,
  rangeStartDay: string,
  rangeEndDay: string
): EventOccurrence[] | null {
  const startDate = new Date(event.start);
  if (isNaN(startDate.getTime())) return null; // unusable start; treat as one-off
  const anchorDay = toLocalDateString(startDate);

  if (!stepOccurrenceDay(anchorDay, recurrence, anchorDay)) return null;

  // recurrenceEnd is INCLUSIVE, and absent on every legacy document — absent
  // means "never ends", NOT "already ended".
  const endDay = event.recurrenceEnd || null;
  if (endDay && endDay < rangeStartDay) return [];

  const endDate = new Date(event.end);
  const hasEnd = !isNaN(endDate.getTime());

  // Shift by WHOLE DAYS with local setDate: preserves the wall-clock time
  // across a DST change (adding a fixed number of ms would not).
  const makeOccurrence = (day: string): EventOccurrence => {
    const shiftDays = dayStringDiff(anchorDay, day);
    const s = new Date(startDate);
    s.setDate(s.getDate() + shiftDays);
    let end = event.end;
    if (hasEnd) {
      const e = new Date(endDate);
      e.setDate(e.getDate() + shiftDays);
      end = event.allDay ? toLocalDateString(e) : e.toISOString();
    }
    return {
      event: {
        ...event,
        start: event.allDay ? toLocalDateString(s) : s.toISOString(),
        end,
      },
      day,
    };
  };

  // Fast-forward to the first occurrence at or after the window start.
  let current = anchorDay;
  if (current < rangeStartDay) {
    const fixed = FIXED_STEP_DAYS[recurrence];
    if (fixed) {
      // Arithmetic, not iteration: a DAILY event created in 2019 would
      // otherwise burn thousands of steps on every render.
      const periods = Math.ceil(dayStringDiff(current, rangeStartDay) / fixed);
      current = addDayString(current, periods * fixed);
    } else {
      let steps = 0;
      while (current < rangeStartDay) {
        if (steps >= MAX_CATCHUP_STEPS) {
          console.warn(
            `[useTodayItems] recurring event ${event.id} exceeded ${MAX_CATCHUP_STEPS} catch-up steps; skipping it`
          );
          return [];
        }
        const next = stepOccurrenceDay(current, recurrence, anchorDay);
        if (!next || next <= current) return []; // non-advancing cursor
        current = next;
        steps++;
      }
    }
  }

  // Days the user deleted individually ("this occurrence only").
  const exceptions = Array.isArray(event.exceptions) ? new Set(event.exceptions) : null;

  const occurrences: EventOccurrence[] = [];
  while (current <= rangeEndDay && occurrences.length < MAX_OCCURRENCES) {
    if (endDay && current > endDay) break; // series finished mid-window
    if (exceptions && exceptions.has(current)) {
      const skipNext = stepOccurrenceDay(current, recurrence, anchorDay);
      if (!skipNext || skipNext <= current) break;
      current = skipNext;
      continue;
    }
    occurrences.push(makeOccurrence(current));
    const next = stepOccurrenceDay(current, recurrence, anchorDay);
    if (!next || next <= current) break;
    current = next;
  }

  return occurrences;
}

// ============================================
// Ordering
// ============================================

/**
 * Display order: day, then all-day items above timed ones (identical to the
 * calendar's own `compareByStart`, so a day panel and a rail read the same),
 * then clock time, then the owner's priority, then id so the list is stable.
 */
function compareTodayItems(a: UnifiedCalendarItem, b: UnifiedCalendarItem): number {
  const aDay = calendarItemDay(a);
  const bDay = calendarItemDay(b);
  if (aDay !== bDay) return aDay < bDay ? -1 : 1;

  const aAllDay = a.allDay ? 0 : 1;
  const bAllDay = b.allDay ? 0 : 1;
  if (aAllDay !== bAllDay) return aAllDay - bAllDay;

  if (!a.allDay && !b.allDay) {
    // NaN (an unparseable timed start) is falsy, so it falls through to the
    // priority tie-break instead of poisoning the sort.
    const byTime = new Date(a.start).getTime() - new Date(b.start).getTime();
    if (byTime) return byTime;
  }

  const byPriority = TODAY_ITEM_PRIORITY[a.type] - TODAY_ITEM_PRIORITY[b.type];
  if (byPriority !== 0) return byPriority;

  return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
}

// ============================================
// Hook
// ============================================

/** Stable empty default so an omitted option cannot churn the memo. */
const NO_REPEATING: RepeatingInvoice[] = [];

/**
 * The local 'YYYY-MM-DD', re-read once a minute so a tab left open overnight
 * rolls over to the new day instead of showing yesterday's list forever. The
 * old composers all captured `new Date()` inside a data-keyed memo, so they
 * only moved on when some unrelated document changed.
 */
function useTodayDay(): string {
  const [day, setDay] = useState(() => toLocalDateString(new Date()));
  useEffect(() => {
    const tick = () => {
      const now = toLocalDateString(new Date());
      setDay(prev => (prev === now ? prev : now));
    };
    const id = window.setInterval(tick, 60000);
    return () => window.clearInterval(id);
  }, []);
  return day;
}

export function useTodayItems(options?: UseTodayItemsOptions): UseTodayItemsResult {
  const {
    events,
    jobs,
    invoices,
    bills,
    eventsLoading,
    jobsLoading,
  } = useAppData();

  const days = options?.days ?? 4;
  const includeSettled = options?.includeSettled ?? false;
  const repeatingInvoices = options?.repeatingInvoices ?? NO_REPEATING;

  // Callers naturally pass an inline `filters` object, so key the merge on its
  // CONTENT rather than its identity — otherwise the whole composition re-runs
  // on every parent render.
  const filterKey = JSON.stringify({ ...DEFAULT_CALENDAR_FILTERS, ...options?.filters });
  const filters = useMemo<CalendarFilterState>(() => JSON.parse(filterKey), [filterKey]);

  const todayDay = useTodayDay();
  const endDay = useMemo(() => addDayString(todayDay, Math.max(0, days)), [todayDay, days]);

  const items = useMemo<UnifiedCalendarItem[]>(() => {
    const collected: UnifiedCalendarItem[] = [];

    /** Keep an item only if it lands inside [todayDay, endDay].
     *  An item with no resolvable day is dropped: a day-scoped rail has
     *  nowhere to put it (the month grid keeps it — that surface can). */
    const inWindow = (item: UnifiedCalendarItem | null): boolean => {
      if (!item) return false;
      const day = calendarItemDay(item);
      return !!day && day >= todayDay && day <= endDay;
    };

    // ── Events ────────────────────────────────────────────────────────────
    if (filters.showEvents) {
      for (const event of events) {
        // Soft delete: legacy documents carry NO status field, so test for
        // 'cancelled' — testing `=== 'active'` would hide every old event.
        if (event.status === 'cancelled') continue;

        const recurrenceType = event.recurrence && event.recurrence.length > 0
          ? event.recurrence[0]
          : null;
        const occurrences = recurrenceType
          ? generateEventOccurrences(event, recurrenceType, todayDay, endDay)
          : null;

        if (occurrences) {
          // Pass the occurrence's own day so each gets a unique id plus
          // `baseEventId` — anything editing the underlying document must use
          // that, or it rewrites/wipes the whole series.
          for (const occ of occurrences) collected.push(eventToCalendarItem(occ.event, occ.day));
          continue;
        }

        const item = eventToCalendarItem(event);
        if (inWindow(item)) collected.push(item);
      }
    }

    // ── Jobs on their due date ────────────────────────────────────────────
    if (filters.showJobs) {
      for (const job of jobs) {
        const item = jobToCalendarItem(job); // null for closed jobs / no due date
        if (inWindow(item)) collected.push(item!);
      }
    }

    // ── Invoices (receivables) ────────────────────────────────────────────
    // No Type guard: AppDataProvider hard-codes Type 'ACCREC' on everything in
    // the `invoices` collection, so the old `Type !== 'ACCREC'` skip in the
    // home rail could never fire. Payables come from `bills` below.
    if (filters.showInvoices) {
      for (const invoice of invoices) {
        if (!includeSettled && isSettled(invoice)) continue;
        const item = filters.invoiceDateMode === 'created'
          ? invoiceCreationToCalendarItem(invoice)
          : invoiceToCalendarItem(invoice);
        if (inWindow(item)) collected.push(item!);
      }
    }

    // ── Recurring invoice/bill templates ──────────────────────────────────
    if (filters.showRecurringInvoices && repeatingInvoices.length > 0) {
      const rangeStart = dayToLocalDate(todayDay);
      const rangeEnd = dayToLocalDate(endDay);
      for (const repeating of repeatingInvoices) {
        const isBillTemplate = repeating.templateType === 'bill';
        if (!isBillTemplate && !filters.recurringShowInvoices) continue;
        if (isBillTemplate && !filters.recurringShowBills) continue;
        for (const occ of repeatingInvoiceToCalendarItems(repeating, rangeStart, rangeEnd)) {
          if (inWindow(occ)) collected.push(occ);
        }
      }
    }

    // ── Bills (accounts payable) ──────────────────────────────────────────
    // `showRecurringExpenses` is a historical misnomer: it gates ACTUAL bills.
    if (filters.showRecurringExpenses) {
      for (const bill of bills) {
        if (!includeSettled && isSettled(bill)) continue;
        const item = billToCalendarItem(bill);
        if (inWindow(item)) collected.push(item!);
      }
    }

    return collected.sort(compareTodayItems);
  }, [events, jobs, invoices, bills, repeatingInvoices, filters, includeSettled, todayDay, endDay]);

  const today = useMemo(
    () => items.filter(item => calendarItemDay(item) === todayDay),
    [items, todayDay]
  );
  const later = useMemo(
    () => items.filter(item => calendarItemDay(item) !== todayDay),
    [items, todayDay]
  );

  return {
    items,
    today,
    later,
    loading: eventsLoading || jobsLoading,
    todayDay,
    endDay,
  };
}

export default useTodayItems;
